import { useState, useCallback, useEffect, useRef } from 'react';
import { logger } from '../../utils/logger';
import { useAuth } from '../auth';
import fetchMigrationsFromService from '../../services/Migration/fetchMigrationsFromService';
import type { Migration } from '../../types';
import fetchOrganizationsFromService from '../../services/Organization/fetchOrganizationsFromService';
import { mapMigrationStatus } from '../../utils/migrationStatus';
import { MOCK_ROLLBACK_MIGRATIONS } from '../../utils/mockRollbackData';
// TODO: REMOVE AFTER MOCKUP — demo-only auto-revert rollback row support.
import {
  getActiveRollbackRows,
  getNextActiveRollbackExpiry
} from '../../utils/demoActiveRollbacks';

// Set to false once the real rollback feature is live and backend is deployed.
const USE_MOCK_ROLLBACK_DEMO = false;

interface UseFetchJobsOptions {
  pollingInterval?: number; // milliseconds
  enablePolling?: boolean;
}

export const useFetchJobs = (options: UseFetchJobsOptions = {}) => {
  const { pollingInterval = 30000, enablePolling = true } = options;

  const [jobs, setJobs] = useState<Migration[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { userInfo, accessToken } = useAuth();
  const pollTimerRef = useRef<NodeJS.Timeout | null>(null);
  // TODO: REMOVE AFTER MOCKUP — timer that triggers a refetch when the next
  // synthetic rollback row expires, so the demo can be replayed.
  const expiryTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isInitialLoadRef = useRef(true);

  const fetchJobs = useCallback(
    async (isBackgroundRefresh = false) => {
      // Only show loading spinner on initial load, not on background polling
      if (!isBackgroundRefresh) {
        setLoading(true);
      }
      setError(null);
      logger.info('Fetching migration jobs ...');
      const started = performance.now();

      try {
        const companyId = userInfo?.orgId || '';

        if (!companyId || !accessToken) {
          throw new Error('Missing customer ID or access token');
        }

        // Fetch all organizations the customer has access to
        const organizations = await fetchOrganizationsFromService(accessToken, companyId);
        const organizationIds = organizations.map((org) => org.id);

        logger.info(`Fetching migrations for ${organizationIds.length} organizations`);

        // Fetch migrations for all organizations
        const migrations = await fetchMigrationsFromService(accessToken, organizationIds);

        // Map backend status values to UI-friendly statuses
        // Backend may send numeric enum values, so we convert them to strings
        const migrationsWithMappedStatus = migrations.map((migration) => ({
          ...migration,
          status: mapMigrationStatus(migration.status) // Always returns string
        }));

        const allJobs: Migration[] = [...migrationsWithMappedStatus];

        // Merge demo rollback rows into the list; deduplicating on refetch.
        // Position is determined by the sort in useSortedJobs (updatedAtUtc desc).
        if (USE_MOCK_ROLLBACK_DEMO) {
          const existingIds = new Set(allJobs.map((j) => j.id));
          for (const mock of MOCK_ROLLBACK_MIGRATIONS) {
            if (!existingIds.has(mock.id)) {
              allJobs.push(mock);
            }
          }
        }

        // ── TODO: REMOVE AFTER MOCKUP ───────────────────────────────────────
        // Demo: merge any in-flight synthetic rollback rows from sessionStorage
        // and schedule a refetch on the next expiry so the dashboard auto-
        // reverts to its original state for demo replay. Delete this whole
        // block (and the demoActiveRollbacks import + expiryTimerRef) when
        // the real rollback feature is wired up.
        const activeRollbackRows = getActiveRollbackRows();
        if (activeRollbackRows.length > 0) {
          const ids = new Set(allJobs.map((j) => j.id));
          for (const r of activeRollbackRows) {
            if (!ids.has(r.id)) allJobs.push(r);
          }
        }
        if (expiryTimerRef.current) {
          clearTimeout(expiryTimerRef.current);
          expiryTimerRef.current = null;
        }
        const nextExpiry = getNextActiveRollbackExpiry();
        if (nextExpiry !== null) {
          const delay = Math.max(0, nextExpiry - Date.now()) + 50;
          expiryTimerRef.current = setTimeout(() => {
            fetchJobs(true);
          }, delay);
        }
        // ── END TODO: REMOVE AFTER MOCKUP ───────────────────────────────────

        setJobs(allJobs);
        logger.info(
          `Successfully fetched ${allJobs.length} migrations in ${(performance.now() - started).toFixed(2)} ms`
        );

        // Mark initial load as complete
        if (isInitialLoadRef.current) {
          isInitialLoadRef.current = false;
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        const normalizedMessage = msg.toLowerCase();

        if (msg.includes('You are not authenticated to access migration logs')) {
          setError(
            'You are not authenticated to access migration logs. Please contact your administrator for assistance.'
          );
          logger.error('Circuit breaker triggered - not authenticated for migration logs', {
            error: msg
          });
        } else if (msg.includes('Authentication token expired')) {
          // Redirect is already in progress from authenticatedFetch; do not show error banner
          logger.warn('Token expired during job fetch - redirect in progress');
        } else if (
          normalizedMessage.includes('403') ||
          normalizedMessage.includes('forbidden')
        ) {
          setError('forbidden');
          logger.error('Failed to fetch migrations', { error: msg });
        } else {
          setError('fetch_failed');
          logger.error('Failed to fetch migrations', { error: msg });
        }

        // Use empty array as fallback - no mock data
        setJobs([]);
      } finally {
        if (!isBackgroundRefresh) {
          setLoading(false);
        }
      }
    },
    [userInfo, accessToken]
  );

  // Check if there are any active jobs that need polling
  // const hasActiveJobs = useCallback((jobsList: Migration[]) => {
  //   const activeStatuses = [
  //     'ExportQueued',
  //     'ExportInProgress',
  //     'TransformationQueued',
  //     'TransformationInProgress',
  //     'ImportQueued',
  //     'ImportInProgress',
  //     'running'
  //   ];
  //   return jobsList.some((job) => activeStatuses.includes(String(job.status)));
  // }, []);

  // Set up polling
  useEffect(() => {
    if (!enablePolling) return;

    // Clear any existing timer
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
    }

    // Start polling at regular intervals
    logger.info(`Starting polling with ${pollingInterval}ms interval`);
    pollTimerRef.current = setInterval(() => {
      fetchJobs(true); // Pass true to indicate background refresh
    }, pollingInterval);

    // Cleanup on unmount or when dependencies change
    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      // TODO: REMOVE AFTER MOCKUP — demo expiry timer cleanup.
      if (expiryTimerRef.current) {
        clearTimeout(expiryTimerRef.current);
        expiryTimerRef.current = null;
      }
    };
  }, [enablePolling, pollingInterval, fetchJobs]);

  return { jobs, setJobs, loading, error, fetchJobs };
};

export default useFetchJobs;
